/**
 * Valentine's gift tags — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_valentines`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const valentinesGiftTagsPack: ToilePack = {
    slug: 'valentines-gift-tags',
    category: 'season',
    name: "Valentine's gift tags",
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'hanging', label: 'Hanging tags', subject: 'a page of six rectangular tags with clipped top corners and a hole circle, each lettered "With Love" over blank "To:" and "From:" lines'},
        {key: 'hearts', label: 'Heart tags', subject: 'a page of six heart-shaped tags with a hole at the top, each lettered "Be Mine" with one blank line beneath for a name'},
        {key: 'treats', label: 'Treat bag tags', subject: 'a page of six round tags for treat bags lettered "Sweets for my Sweet" with a blank "From:" line and a ring of tiny hearts around the edge'},
    ],
    styles: [
        {key: 'kraft', label: 'Kraft paper', style: 'hand-stamped kraft tag look, simple ink heart stamps and a twine-loop hole circle', palette: 'brown kraft with dark red ink', framing: 'square page with the tags in a two-by-three grid, solid cut outlines, a wide margin'},
        {key: 'pastel', label: 'Pastel candy', style: 'candy-colored tags with scalloped edges, polka dots and small hearts', palette: 'pink, peach, mint and cream', framing: 'square page with the tags in a two-by-three grid, solid cut outlines, a wide margin'},
        {key: 'foil', label: 'Red and gold', style: 'bold red tags with gold foil-look hearts and a thin gold border', palette: 'deep red and metallic gold', framing: 'square page with the tags in a two-by-three grid, solid cut outlines, a wide margin'},
    ],
    directions: ['for teachers', 'with flowers', 'jar labels'],
    directionHint: 'try: for teachers',
    seo: {
        title: "Valentine's gift tags: hanging, heart, treat bag",
        metaDescription: "Nine printable Valentine's gift tags, hanging, heart and treat bag, six to a page, in kraft, pastel and red-and-gold styles.",
        h1: "Valentine's gift tags: hanging tags, heart tags and treat bag tags",
        tagline: 'Six tags to a page, cut out and tied on, in kraft, pastel and red and gold.',
        intro: "Valentine's gift tags are printed, cut out and tied onto a gift, a jar of sweets or a bunch of flowers. Each page holds six tags with a hole mark and blank To and From lines. The designs are a With Love tag, a Be Mine heart and a Sweets for my Sweet treat bag tag, in kraft, pastel candy or red and gold. Type a use, such as for teachers, and the tag takes it.",
        useCases: [
            "a tag on a Valentine's gift",
            'a label on a jar of sweets',
            'a card tied to a bunch of flowers',
            'a classroom gift exchange',
        ],
        faq: [
            {q: 'How many tags are on one page?', a: 'Six, each with a hole mark for string or ribbon.'},
            {q: 'What does each tag say?', a: 'With Love on the hanging tag, Be Mine on the heart tag and Sweets for my Sweet on the treat bag tag. The To and From lines are blank for names.'},
            {q: 'How do I make the tags?', a: 'Print the page, cut out each tag along its edge, and thread ribbon or string through the hole mark.'},
            {q: 'How do the three styles differ?', a: 'Kraft paper has stamped hearts and a twine loop. Pastel candy has scalloped edges and polka dots, and red and gold has a thin gold border with foil-look hearts.'},
        ],
    },
}
